'use client';
import React, { useState } from 'react';
import { ChevronLeft, ChevronRight, ArrowRight, Clock, AlertCircle } from 'lucide-react';
import type { BookingDateTime } from './BookingWizard';

// Generate a calendar for a given month
function getDaysInMonth(year: number, month: number) {
  const firstDay = new Date(year, month, 1).getDay();
  const daysInMonth = new Date(year, month + 1, 0).getDate();
  return { firstDay, daysInMonth };
}

// Blocked dates (mock — BACKEND INTEGRATION: GET /api/availability/blocked)
const BLOCKED_DATES = [5, 6, 12, 13, 19, 20, 26, 27]; // weekends are blocked for demo
const FULLY_BOOKED = [8, 15, 22];

// All possible time slots with their start hour (24h)
const ALL_TIME_SLOTS = [
  { id: 'slot-1000', time: '10:00 AM', startHour: 10, available: true },
  { id: 'slot-1100', time: '11:00 AM', startHour: 11, available: true },
  { id: 'slot-1200', time: '12:00 PM', startHour: 12, available: true },
  { id: 'slot-1300', time: '1:00 PM', startHour: 13, available: false },
  { id: 'slot-1400', time: '2:00 PM', startHour: 14, available: true },
  { id: 'slot-1500', time: '3:00 PM', startHour: 15, available: true },
  { id: 'slot-1600', time: '4:00 PM', startHour: 16, available: false },
];

// Business closes at 18:00 (6 PM)
const BUSINESS_CLOSE_HOUR = 18;

const MONTH_NAMES = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];
const DAY_NAMES = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

interface Props {
  onSelect: (dt: BookingDateTime) => void;
  onBack: () => void;
  selected: BookingDateTime | null;
  serviceDurationHours: number;
}

export default function StepDateTime({ onSelect, onBack, selected, serviceDurationHours }: Props) {
  const today = new Date(2026, 4, 2); // May 2, 2026
  const [viewYear, setViewYear] = useState(2026);
  const [viewMonth, setViewMonth] = useState(4); // May
  const [selectedDay, setSelectedDay] = useState<number | null>(null);
  const [selectedSlot, setSelectedSlot] = useState<string | null>(null);

  const { firstDay, daysInMonth } = getDaysInMonth(viewYear, viewMonth);

  // Filter slots: only show slots where start + duration fits before close of business
  const TIME_SLOTS = ALL_TIME_SLOTS.map((slot) => ({
    ...slot,
    available: slot.available && (slot.startHour + serviceDurationHours) <= BUSINESS_CLOSE_HOUR,
  }));

  const prevMonth = () => {
    if (viewMonth === 0) { setViewMonth(11); setViewYear((y) => y - 1); }
    else setViewMonth((m) => m - 1);
  };

  const nextMonth = () => {
    if (viewMonth === 11) { setViewMonth(0); setViewYear((y) => y + 1); }
    else setViewMonth((m) => m + 1);
  };

  const isBlocked = (day: number) => {
    const d = new Date(viewYear, viewMonth, day);
    return d.getDay() === 0 || d.getDay() === 6 || FULLY_BOOKED.includes(day);
  };

  const isPast = (day: number) => {
    const d = new Date(viewYear, viewMonth, day);
    return d < today;
  };

  const handleContinue = () => {
    if (!selectedDay || !selectedSlot) return;
    const slot = TIME_SLOTS.find((s) => s.id === selectedSlot);
    const dateStr = `${MONTH_NAMES[viewMonth]} ${selectedDay}, ${viewYear}`;
    onSelect({ date: dateStr, time: slot?.time ?? '' });
  };

  // Duration label for display
  const durationLabel = serviceDurationHours === 1 ? '1 hour' : `${serviceDurationHours} hours`;

  return (
    <div className="animate-fade-in">
      <div className="mb-8">
        <h1 className="text-3xl font-extrabold text-foreground mb-2">Pick a Date & Time</h1>
        <p className="text-muted-foreground">Available slots shown in real-time. Weekends and fully-booked days are unavailable.</p>
        <div className="mt-3 inline-flex items-center gap-2 px-3 py-1.5 rounded-lg bg-primary/10 border border-primary/20 text-xs font-semibold text-primary">
          <Clock size={13} />
          Service duration: {durationLabel} — only slots with enough time are shown
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Calendar */}
        <div className="bg-card border border-border rounded-xl p-5">
          <div className="flex items-center justify-between mb-5">
            <button
              onClick={prevMonth}
              className="p-2 rounded-lg text-muted-foreground hover:text-foreground hover:bg-muted/50 transition-all duration-150 active:scale-95"
              aria-label="Previous month"
            >
              <ChevronLeft size={18} />
            </button>
            <p className="text-base font-bold text-foreground">
              {MONTH_NAMES[viewMonth]} {viewYear}
            </p>
            <button
              onClick={nextMonth}
              className="p-2 rounded-lg text-muted-foreground hover:text-foreground hover:bg-muted/50 transition-all duration-150 active:scale-95"
              aria-label="Next month"
            >
              <ChevronRight size={18} />
            </button>
          </div>

          {/* Day headers */}
          <div className="grid grid-cols-7 mb-2">
            {DAY_NAMES.map((d) => (
              <div key={`day-header-${d}`} className="text-center text-xs font-semibold text-muted-foreground py-1">
                {d}
              </div>
            ))}
          </div>

          {/* Days */}
          <div className="grid grid-cols-7 gap-1">
            {Array.from({ length: firstDay }).map((_, i) => (
              <div key={`empty-${i}`} />
            ))}
            {Array.from({ length: daysInMonth }).map((_, i) => {
              const day = i + 1;
              const blocked = isBlocked(day);
              const past = isPast(day);
              const isSelected = selectedDay === day;
              const isToday = day === today.getDate() && viewMonth === today.getMonth() && viewYear === today.getFullYear();

              return (
                <button
                  key={`cal-day-${day}`}
                  onClick={() => { if (!blocked && !past) { setSelectedDay(day); setSelectedSlot(null); } }}
                  disabled={blocked || past}
                  className={`relative aspect-square flex items-center justify-center rounded-lg text-sm font-medium transition-all duration-150 ${
                    isSelected
                      ? 'gold-gradient text-primary-foreground shadow-gold'
                      : blocked || past
                      ? 'text-muted-foreground/30 cursor-not-allowed'
                      : isToday
                      ? 'border border-primary/50 text-primary hover:bg-primary/10' :'text-foreground hover:bg-muted/50'
                  }`}
                >
                  {day}
                  {FULLY_BOOKED.includes(day) && !past && (
                    <span className="absolute bottom-0.5 left-1/2 -translate-x-1/2 w-1 h-1 rounded-full bg-red-400" />
                  )}
                </button>
              );
            })}
          </div>

          {/* Legend */}
          <div className="mt-4 pt-4 border-t border-border flex flex-wrap gap-4 text-xs text-muted-foreground">
            <div className="flex items-center gap-1.5">
              <div className="w-2.5 h-2.5 rounded-sm gold-gradient" />
              Selected
            </div>
            <div className="flex items-center gap-1.5">
              <div className="w-2.5 h-2.5 rounded-full bg-red-400" />
              Fully booked
            </div>
            <div className="flex items-center gap-1.5">
              <div className="w-2.5 h-2.5 rounded-sm bg-muted-foreground/20" />
              Unavailable
            </div>
          </div>
        </div>

        {/* Time slots */}
        <div className="bg-card border border-border rounded-xl p-5">
          <div className="flex items-center gap-2 mb-5">
            <Clock size={16} className="text-primary" />
            <h3 className="text-sm font-bold text-foreground">
              {selectedDay
                ? `Available Times — ${MONTH_NAMES[viewMonth]} ${selectedDay}`
                : 'Select a date to see available times'}
            </h3>
          </div>

          {!selectedDay ? (
            <div className="flex flex-col items-center justify-center h-48 text-center">
              <div className="w-12 h-12 rounded-xl bg-muted/40 flex items-center justify-center mb-3">
                <Clock size={22} className="text-muted-foreground" />
              </div>
              <p className="text-sm text-muted-foreground">Pick a date on the calendar to see available time slots</p>
            </div>
          ) : (
            <>
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                {TIME_SLOTS.map((slot) => {
                  const isChosen = selectedSlot === slot.id;
                  const endHour = slot.startHour + serviceDurationHours;
                  const endLabel = endHour >= 12
                    ? `${endHour > 12 ? endHour - 12 : endHour}:00 ${endHour >= 12 ? 'PM' : 'AM'}`
                    : `${endHour}:00 AM`;
                  return (
                    <button
                      key={slot.id}
                      onClick={() => { if (slot.available) setSelectedSlot(slot.id); }}
                      disabled={!slot.available}
                      className={`py-3 px-2 rounded-lg border text-sm font-semibold transition-all duration-150 active:scale-95 flex flex-col items-center gap-0.5 ${
                        isChosen
                          ? 'border-primary bg-primary/10 text-primary'
                          : slot.available
                          ? 'border-border text-foreground hover:border-primary/40 hover:bg-muted/40'
                          : 'border-border/30 text-muted-foreground/30 cursor-not-allowed bg-muted/10 line-through'
                      }`}
                    >
                      <span>{slot.time}</span>
                      {slot.available && (
                        <span className="text-[10px] font-normal opacity-60">ends {endLabel}</span>
                      )}
                    </button>
                  );
                })}
              </div>
            </>
          )}

          {selectedSlot && (
            <div className="mt-4 pt-4 border-t border-border flex items-center gap-2 text-xs text-muted-foreground">
              <AlertCircle size={13} className="text-amber-400 flex-shrink-0" />
              Slot held for 10 minutes. Complete your booking to confirm.
            </div>
          )}
        </div>
      </div>

      {/* Navigation */}
      <div className="flex items-center gap-4 mt-8">
        <button
          onClick={onBack}
          className="flex items-center gap-2 px-5 py-3 rounded-xl border border-border text-sm font-semibold text-muted-foreground hover:text-foreground hover:bg-muted/40 transition-all duration-150 active:scale-95"
        >
          <ChevronLeft size={16} />
          Back
        </button>
        <button
          onClick={handleContinue}
          disabled={!selectedDay || !selectedSlot}
          className={`flex items-center gap-2 px-8 py-3 rounded-xl text-base font-bold transition-all duration-150 active:scale-95 ${
            selectedDay && selectedSlot
              ? 'gold-gradient text-primary-foreground hover:opacity-90 shadow-gold'
              : 'bg-muted text-muted-foreground cursor-not-allowed'
          }`}
        >
          Continue to Your Details
          <ArrowRight size={18} />
        </button>
      </div>
    </div>
  );
}